import { useEffect, useState } from 'react'
import { api } from '@/util/api'
import type {
  CreateFeedbackItemPayload,
  FeedbackCommentsResponse,
  FeedbackFilters,
  FeedbackItemResponse,
  FeedbackItemResource,
  FeedbackItemsResponse,
} from '@/types/feedback'

export function useFeedbackItems(filters: FeedbackFilters) {
  const [items, setItems] = useState<FeedbackItemResource[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const response = await api.listFeedbackItems(filters)
        if (active) setItems(response.data)
      } catch (err: any) {
        if (active) setError(err?.response?.data?.error?.message || 'Failed to load feedback')
      } finally {
        if (active) setLoading(false)
      }
    }

    load()

    return () => {
      active = false
    }
  }, [filters.following, filters.kind, filters.query, filters.reporter, filters.sort, filters.status])

  return { items, loading, error, setItems }
}

export function useFeedbackItem(feedbackItemId?: string) {
  const [item, setItem] = useState<FeedbackItemResource | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!feedbackItemId) return
    const currentFeedbackItemId = feedbackItemId

    let active = true

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const response = await api.getFeedbackItem(currentFeedbackItemId)
        if (active) setItem(response.data)
      } catch (err: any) {
        if (active) setError(err?.response?.data?.error?.message || 'Failed to load feedback item')
      } finally {
        if (active) setLoading(false)
      }
    }

    load()

    return () => {
      active = false
    }
  }, [feedbackItemId])

  return { item, loading, error, setItem }
}

export function useFeedbackRoadmap() {
  const [items, setItems] = useState<FeedbackItemResource[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const response = await api.getFeedbackRoadmap()
        if (active) setItems(response.data)
      } catch (err: any) {
        if (active) setError(err?.response?.data?.error?.message || 'Failed to load roadmap')
      } finally {
        if (active) setLoading(false)
      }
    }

    load()

    return () => {
      active = false
    }
  }, [])

  return { items, loading, error, setItems }
}

export function useFeedbackActions() {
  return {
    createFeedbackItem(payload: CreateFeedbackItemPayload) {
      return api.createFeedbackItem(payload)
    },
    updateFeedbackItem(id: string, payload: Partial<CreateFeedbackItemPayload>) {
      return api.updateFeedbackItem(id, payload)
    },
    changeFeedbackStatus(id: string, status: string, statusReason?: string) {
      return api.changeFeedbackStatus(id, status, statusReason)
    },
    changeFeedbackPriority(id: string, priority: string) {
      return api.changeFeedbackPriority(id, priority)
    },
    addFeedbackComment(id: string, body_markdown: string) {
      return api.createFeedbackComment(id, body_markdown)
    },
    followFeedbackItem(id: string) {
      return api.followFeedbackItem(id)
    },
    unfollowFeedbackItem(id: string) {
      return api.unfollowFeedbackItem(id)
    },
    voteFeedbackItem(id: string) {
      return api.voteFeedbackItem(id)
    },
    unvoteFeedbackItem(id: string) {
      return api.unvoteFeedbackItem(id)
    },
    listFeedbackComments(id: string) {
      return api.listFeedbackComments(id)
    },
  }
}

export type { FeedbackItemsResponse, FeedbackItemResponse, FeedbackCommentsResponse }
